import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, RefreshCw, Trash2, Calendar as CalendarIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";

const today = () => new Date().toISOString().slice(0, 10);

export default function ManagerTherapists() {
  const { branch } = useAuth();
  const [therapists, setTherapists] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [date, setDate] = useState(today());
  const [addOpen, setAddOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, s] = await Promise.all([
        api.get("/therapists", { params: { branch_code: branch.code } }),
        api.get("/therapist-schedule", { params: { branch_code: branch.code, date } }),
      ]);
      setTherapists(t.data);
      setSchedule(s.data);
    } catch (err) { toast.error(formatApiError(err, "Load failed")); }
    finally { setLoading(false); }
  }, [branch?.code, date]);

  useEffect(() => { load(); }, [load]);

  const delTherapist = async (id) => {
    if (!window.confirm("Delete this therapist?")) return;
    try { await api.delete(`/therapists/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err, "Failed")); }
  };
  const delSlot = async (id) => {
    try { await api.delete(`/therapist-schedule/${id}`); toast.success("Slot removed"); load(); }
    catch (err) { toast.error(formatApiError(err, "Failed")); }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="therapists-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">Manager · Therapists · {branch.name}</div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">Panchakarma therapists &amp; rota</h1>
          <p className="text-[#5C5652]">Maintain the therapist bench and assign them to therapy slots.</p>
        </div>
        <button onClick={load} data-testid="therapists-refresh" className="h-10 px-4 rounded-lg border border-sand bg-white text-sm font-semibold inline-flex items-center gap-2"><RefreshCw className="h-4 w-4" /> Refresh</button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Roster */}
        <div className="bg-white border border-sand rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-sand flex items-center justify-between">
            <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652]">Therapists ({therapists.length})</div>
            <button onClick={() => setAddOpen(true)} data-testid="add-therapist" className="text-sm text-forest font-semibold hover:underline inline-flex items-center gap-1"><Plus className="h-3.5 w-3.5" /> Add</button>
          </div>
          {therapists.length === 0 ? <div className="p-6 text-sm text-[#5C5652]">No therapists yet.</div> : (
            <ul className="divide-y divide-[#E8E3D9]" data-testid="therapists-list">
              {therapists.map(t => (
                <li key={t.id} className="px-6 py-3 flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[#2A2624]">{t.name}</div>
                    <div className="text-xs text-[#5C5652]">{t.phone || "—"} · {(t.skills || []).join(", ") || "General"}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mt-1">Branches: {(t.branches || []).join(", ") || "—"}</div>
                  </div>
                  <button onClick={() => delTherapist(t.id)} data-testid={`delete-therapist-${t.id}`} className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><Trash2 className="h-3.5 w-3.5 text-[#C25946]" /></button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Schedule */}
        <div className="bg-white border border-sand rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-sand flex items-center justify-between gap-3">
            <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652]">Schedule</div>
            <div className="flex gap-2">
              <div className="relative">
                <CalendarIcon className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#5C5652]" />
                <input data-testid="schedule-date" type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className="h-8 pl-7 pr-2 rounded-md border border-sand bg-white text-xs focus:outline-none" />
              </div>
              <button onClick={() => setScheduleOpen(true)} disabled={therapists.length === 0} data-testid="add-slot"
                className="text-sm text-forest font-semibold hover:underline inline-flex items-center gap-1 disabled:opacity-50"><Plus className="h-3.5 w-3.5" /> Slot</button>
            </div>
          </div>
          {schedule.length === 0 ? <div className="p-6 text-sm text-[#5C5652]">No slots for this date.</div> : (
            <ul className="divide-y divide-[#E8E3D9]" data-testid="schedule-list">
              {schedule.map(s => {
                const t = therapists.find(x => x.id === s.therapist_id);
                return (
                  <li key={s.id} className="px-6 py-3 flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono text-forest">{s.time_slot}</div>
                      <div className="font-semibold text-[#2A2624]">{s.patient_name} · {s.therapy_type}</div>
                      <div className="text-xs text-[#5C5652]">Therapist: {t?.name || s.therapist_id} {s.room_number && <>· Room {s.room_number}</>}</div>
                    </div>
                    <button onClick={() => delSlot(s.id)} data-testid={`delete-slot-${s.id}`} className="h-8 w-8 rounded-md border border-sand hover:bg-[#F9F6F0] flex items-center justify-center"><Trash2 className="h-3.5 w-3.5 text-[#C25946]" /></button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {addOpen && <TherapistModal onClose={() => setAddOpen(false)} onSaved={() => { setAddOpen(false); load(); }} />}
      {scheduleOpen && <ScheduleModal therapists={therapists} branch={branch} date={date} onClose={() => setScheduleOpen(false)} onSaved={() => { setScheduleOpen(false); load(); }} />}
    </div>
  );
}

function TherapistModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", phone: "", skills: "", branches: [] });
  const [busy, setBusy] = useState(false);
  const toggleBranch = (b) => setForm(f => ({ ...f, branches: f.branches.includes(b) ? f.branches.filter(x => x !== b) : [...f.branches, b] }));
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      await api.post("/therapists", { name: form.name.trim(), phone: form.phone,
        branches: form.branches, skills: form.skills.split(",").map(s => s.trim()).filter(Boolean) });
      toast.success("Therapist added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Portal>
    <div className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="therapist-modal">
        <div className="font-display text-2xl text-forest mb-4">Add therapist</div>
        <div className="space-y-3">
          <input data-testid="th-name" placeholder="Full name" value={form.name} onChange={(e) => setForm(f => ({...f, name: e.target.value}))} className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="th-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm(f => ({...f, phone: e.target.value}))} className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="th-skills" placeholder="Skills (comma separated, e.g., Abhyanga, Shirodhara)" value={form.skills} onChange={(e) => setForm(f => ({...f, skills: e.target.value}))} className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-[#5C5652] mb-2">Branches</div>
            <div className="flex flex-wrap gap-1.5" data-testid="th-branches">
              {["PBY","CHP","VZK","BCS","BPC","BNE"].map(b => (
                <button key={b} type="button" onClick={() => toggleBranch(b)} data-testid={`th-branch-${b}`}
                  className={`h-8 px-2.5 rounded-md text-xs font-bold border ${form.branches.includes(b) ? "bg-forest text-white border-forest" : "border-sand text-[#5C5652]"}`}>{b}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={busy} data-testid="th-save" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">Save</button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}

function ScheduleModal({ therapists, branch, date, onClose, onSaved }) {
  const [form, setForm] = useState({
    therapist_id: therapists[0]?.id || "", patient_name: "", room_number: "",
    therapy_type: "Abhyanga", time_slot: "10:00-11:00",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.therapist_id || !form.patient_name.trim() || !form.therapy_type || !form.time_slot) { toast.error("Fill all fields"); return; }
    setBusy(true);
    try {
      await api.post("/therapist-schedule", { ...form, patient_name: form.patient_name.trim(), branch_code: branch.code, date });
      toast.success("Slot added");
      onSaved();
    } catch (err) { toast.error(formatApiError(err, "Failed")); }
    finally { setBusy(false); }
  };
  return (
    <Portal>
    <div className="fixed inset-0 bg-[#2A2624]/50 z-40 flex items-center justify-center p-6" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-lg border border-sand p-6" onClick={(e) => e.stopPropagation()} data-testid="schedule-modal">
        <div className="text-[10px] uppercase tracking-wider text-[#5C5652] mb-1">{date}</div>
        <div className="font-display text-2xl text-forest mb-4">New therapy slot</div>
        <div className="space-y-3">
          <select data-testid="slot-therapist" value={form.therapist_id} onChange={(e) => setForm(f => ({...f, therapist_id: e.target.value}))}
            className="w-full h-11 px-3 rounded-lg border border-sand bg-white focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none">
            {therapists.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          <input data-testid="slot-patient" placeholder="Patient name" value={form.patient_name} onChange={(e) => setForm(f => ({...f, patient_name: e.target.value}))} className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="slot-room" placeholder="Room number (optional)" value={form.room_number} onChange={(e) => setForm(f => ({...f, room_number: e.target.value}))} className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="slot-therapy" placeholder="Therapy (e.g., Abhyanga, Shirodhara)" value={form.therapy_type} onChange={(e) => setForm(f => ({...f, therapy_type: e.target.value}))} className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
          <input data-testid="slot-time" placeholder="Time slot (e.g., 10:00-11:00)" value={form.time_slot} onChange={(e) => setForm(f => ({...f, time_slot: e.target.value}))} className="w-full h-11 px-3 rounded-lg border border-sand focus:ring-2 focus:ring-[#1A4331]/40 focus:outline-none" />
        </div>
        <div className="mt-5 flex gap-3">
          <button onClick={save} disabled={busy} data-testid="slot-save" className="flex-1 h-11 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover disabled:opacity-60">Add slot</button>
          <button onClick={onClose} className="h-11 px-4 rounded-lg border border-sand font-semibold hover:bg-[#F9F6F0]">Cancel</button>
        </div>
      </div>
    </div>
    </Portal>
  );
}
